/**
 * ClaimRoutingTab - Settings > Claims > Routing.
 *
 * Two sections over one rule store: DIRECT rules (the creation-time
 * fallback chain - who gets a new claim) and CONDITIONAL rules (re-route
 * when a state is reached and conditions match). The backend keeps the
 * workflow side-effect wiring in sync on every save, so this page only
 * manages ClaimRoutingRule rows. Nothing here is tenant vocabulary - all
 * options come from the meta endpoint and the org's roles/members.
 */

import { useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'

import {
  useRoutingMeta,
  useRoutingRules,
  type RoutingKind,
  type RoutingRule,
} from '@/admin/hooks/use-routing-rules'
import { useFeatureFlags } from '@/admin/hooks/use-feature-flag'
import { ConditionalRoutingSection } from './conditional-routing-section'
import { ConditionalRuleDialog } from './conditional-rule-dialog'
import { CoverageSection } from './coverage-section'
import { DirectRoutingSection } from './direct-routing-section'
import { DirectRuleDialog } from './direct-rule-dialog'

interface DialogState {
  kind: RoutingKind
  editing: RoutingRule | null
}

export function ClaimRoutingTab() {
  const { data: rules, isPending, isError } = useRoutingRules()
  const { data: meta, isPending: metaPending, isError: metaError } = useRoutingMeta()
  const [dialog, setDialog] = useState<DialogState | null>(null)
  const flags = useFeatureFlags()
  const advancedRouting = flags['advanced_routing'] === true

  const directRules = useMemo(
    () => (rules ?? []).filter((rule) => rule.kind === 'direct'),
    [rules],
  )
  const conditionalRules = useMemo(
    () => (rules ?? []).filter((rule) => rule.kind === 'conditional'),
    [rules],
  )

  if (isPending || metaPending) {
    return (
      <div className="flex items-center gap-2 py-10 text-[13px] text-[var(--ink-3)]">
        <Loader2 size={14} className="animate-spin" />
        Loading routing rules...
      </div>
    )
  }

  if (isError || metaError || !meta) {
    return (
      <p className="py-10 text-[13px] text-[var(--ink-3)]">
        Routing rules could not be loaded. The claims workflow may not be
        configured for this organisation yet.
      </p>
    )
  }

  return (
    <div className="flex max-w-[960px] flex-col gap-10">
      <header>
        <h2 className="font-display text-[28px] font-medium leading-[1.1] text-[var(--ink)]">
          Claim routing
        </h2>
        <p className="mt-1 text-[13px] text-[var(--ink-2)]">
          Decide who a claim goes to when it is created, and re-route it
          automatically when conditions match.
        </p>
      </header>

      <DirectRoutingSection
        rules={directRules}
        meta={meta}
        onAdd={() => setDialog({ kind: 'direct', editing: null })}
        onEdit={(rule) => setDialog({ kind: 'direct', editing: rule })}
      />

      <ConditionalRoutingSection
        rules={conditionalRules}
        meta={meta}
        onAdd={() => setDialog({ kind: 'conditional', editing: null })}
        onEdit={(rule) => setDialog({ kind: 'conditional', editing: rule })}
      />

      {/* Out-of-office coverage - gated on advanced_routing (the same internal
          capability the Claim Team surface uses); data-driven, no slug. */}
      {advancedRouting && <CoverageSection />}

      {dialog?.kind === 'direct' && (
        <DirectRuleDialog
          key={dialog.editing?.uuid ?? 'add-direct'}
          editing={dialog.editing}
          meta={meta}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'conditional' && (
        <ConditionalRuleDialog
          key={dialog.editing?.uuid ?? 'add-conditional'}
          editing={dialog.editing}
          meta={meta}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  )
}
